import ConversationFollowUpsAPI from '../../api/conversationFollowUps';
import types from '../mutation-types';

export const state = {
  conversationId: null,
  flow: null,
  enrollment: null,
  previewFirstSendAt: null,
  // Conversations whose enrollment/management POST is in flight. Managing is
  // a LIST: the contact profile can act on cycles of several conversations,
  // and finishing one must not re-enable another still in flight.
  enrollingConversationId: null,
  managingConversationIds: [],
  // Last cable event about any follow-up cycle: views that aggregate cycles
  // (the contact profile tab) watch this to invalidate themselves.
  liveEvent: { conversationId: null, at: 0 },
  uiFlags: {
    isFetching: false,
    isError: false,
  },
};

// Identity of the newest fetch: a slower response from a previously selected
// conversation must never overwrite the state of the current one. The target
// is set synchronously at dispatch time, so an enroll that finishes after
// the user navigated away also knows it went stale.
let fetchGeneration = 0;
let activeConversationId = null;

export const getters = {
  getState: $state => $state,
  getUIFlags: $state => $state.uiFlags,
};

export const actions = {
  async fetch({ commit }, conversationId) {
    activeConversationId = conversationId;
    fetchGeneration += 1;
    const generation = fetchGeneration;
    commit(types.SET_CONVERSATION_FOLLOW_UP_UI_FLAG, {
      isFetching: true,
      isError: false,
    });
    try {
      const response =
        await ConversationFollowUpsAPI.getEnrollment(conversationId);
      if (generation !== fetchGeneration) return;
      commit(types.SET_CONVERSATION_FOLLOW_UP, {
        conversationId,
        flow: response.data.flow || null,
        enrollment: response.data.enrollment || null,
        previewFirstSendAt: response.data.preview_first_send_at || null,
      });
    } catch (error) {
      if (generation === fetchGeneration) {
        commit(types.SET_CONVERSATION_FOLLOW_UP_UI_FLAG, { isError: true });
      }
      throw error;
    } finally {
      if (generation === fetchGeneration) {
        commit(types.SET_CONVERSATION_FOLLOW_UP_UI_FLAG, { isFetching: false });
      }
    }
  },
  async enroll({ commit, dispatch, state: $state }, conversationId) {
    commit(types.SET_CONVERSATION_FOLLOW_UP_ENROLLING, conversationId);
    try {
      await ConversationFollowUpsAPI.enroll(conversationId);
      // Refetch only while this conversation is still the panel's target: a
      // POST that lands after navigating away must not repopulate the store
      // with the old conversation.
      if (activeConversationId === conversationId) {
        await dispatch('fetch', conversationId);
      }
    } finally {
      if ($state.enrollingConversationId === conversationId) {
        commit(types.SET_CONVERSATION_FOLLOW_UP_ENROLLING, null);
      }
    }
  },
  // pause | resume | remove on the DISPLAYED cycle: enrollmentId binds the
  // operation to the card the user acted on — the backend conflicts if that
  // cycle is no longer the one in progress.
  async manage(
    { commit, dispatch },
    { conversationId, enrollmentId, operation }
  ) {
    if (!['pause', 'resume', 'remove'].includes(operation)) {
      throw new Error(`Unknown follow-up management operation: ${operation}`);
    }
    commit(types.SET_CONVERSATION_FOLLOW_UP_MANAGING, {
      conversationId,
      managing: true,
    });
    try {
      await ConversationFollowUpsAPI[operation](conversationId, enrollmentId);
      if (activeConversationId === conversationId) {
        await dispatch('fetch', conversationId);
      }
    } finally {
      commit(types.SET_CONVERSATION_FOLLOW_UP_MANAGING, {
        conversationId,
        managing: false,
      });
    }
  },
  // ActionCable: the backend publishes after committing an enrollment/step
  // transition, so refetching here always reads the new state — this event,
  // not any timer, is the panel's consistency signal.
  receiveLiveUpdate({ commit, dispatch }, followUp) {
    commit(types.SET_CONVERSATION_FOLLOW_UP_LIVE_EVENT, {
      conversationId: Number(followUp.conversation_id),
      at: Date.now(),
    });
    if (Number(followUp.conversation_id) !== Number(activeConversationId))
      return;
    dispatch('fetch', activeConversationId).catch(() => {});
  },
};

export const mutations = {
  [types.SET_CONVERSATION_FOLLOW_UP_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.SET_CONVERSATION_FOLLOW_UP_ENROLLING]($state, conversationId) {
    $state.enrollingConversationId = conversationId;
  },
  [types.SET_CONVERSATION_FOLLOW_UP_MANAGING](
    $state,
    { conversationId, managing }
  ) {
    const next = new Set($state.managingConversationIds);
    if (managing) next.add(conversationId);
    else next.delete(conversationId);
    $state.managingConversationIds = [...next];
  },
  [types.SET_CONVERSATION_FOLLOW_UP_LIVE_EVENT]($state, liveEvent) {
    $state.liveEvent = liveEvent;
  },
  [types.SET_CONVERSATION_FOLLOW_UP](
    $state,
    { conversationId, flow, enrollment, previewFirstSendAt }
  ) {
    $state.conversationId = conversationId;
    $state.flow = flow;
    $state.enrollment = enrollment;
    $state.previewFirstSendAt = previewFirstSendAt;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
